import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { ProjectContext } from "@/components/project-context";
import { ProjectAccessProvider } from "@/lib/project-access";
import { ProjectNoAccess, ProjectNotFound } from "./project-not-found";
import { EntityErrorState } from "@/components/layout/entity-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useProjectLiveUpdates } from "@/hooks/use-project-live-updates";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ProjectShell() {
  const { id } = useParams<{ id: string }>();
  const isValidUuid = typeof id === "string" && UUID_REGEX.test(id);

  const {
    data: project,
    isLoading,
    error,
    refetch,
  } = trpc.project.getById.useQuery({ id: id ?? "" }, { enabled: isValidUuid });

  useProjectLiveUpdates(project?.id);

  // Persist last visited project on the profile (fire-and-forget, once per session per project)
  const setDefaultProject = trpc.profile.setDefaultProject.useMutation();
  useEffect(() => {
    if (!project?.id) return;
    try {
      const key = "iamu:default_project";
      if (sessionStorage.getItem(key) === project.id) return;
      sessionStorage.setItem(key, project.id);
      setDefaultProject.mutate({ projectId: project.id });
    } catch {
      // Ignore storage errors in private browsing
      setDefaultProject.mutate({ projectId: project.id });
    }
  }, [project?.id, setDefaultProject]);

  if (!isValidUuid) {
    return <ProjectNotFound />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error?.data?.code === "FORBIDDEN") {
    return <ProjectNoAccess />;
  }

  if (error?.data?.code === "NOT_FOUND" || (!project && !error)) {
    return <ProjectNotFound />;
  }

  if (error) {
    return (
      <EntityErrorState
        title="Failed to load project"
        description="We couldn't connect to the server or load the project. Please check your connection and try again."
        onRetry={() => {
          refetch();
        }}
        backLink={{ to: "/projects", label: "Back to projects" }}
      />
    );
  }

  return (
    <ProjectContext.Provider value={project}>
      <ProjectAccessProvider projectId={project.id}>
        <Outlet />
      </ProjectAccessProvider>
    </ProjectContext.Provider>
  );
}
